import axios from "axios";

/**
 * Perplexity API Service for Frontend
 * Provides up-to-date information search capabilities
 */

/**
 * Lấy Perplexity API key theo cơ chế round-robin
 * @returns {string} API key hiện tại
 */
function getPerplexityApiKey() {
  const keys = [
    process.env.REACT_APP_PERPLEXITY_API_KEY_1,
    process.env.REACT_APP_PERPLEXITY_API_KEY_2,
    process.env.REACT_APP_PERPLEXITY_API_KEY_3,
  ].filter(Boolean); // Lọc bỏ các key undefined/null

  if (keys.length === 0) {
    throw new Error("Không tìm thấy Perplexity API key nào trong cấu hình");
  }

  // Sử dụng biến toàn cục để theo dõi index
  if (typeof window.currentPerplexityKeyIndex === "undefined") {
    window.currentPerplexityKeyIndex = 0;
  }

  // Lấy key hiện tại và tăng index
  const key = keys[window.currentPerplexityKeyIndex];
  window.currentPerplexityKeyIndex =
    (window.currentPerplexityKeyIndex + 1) % keys.length;

  return key;
}

/**
 * Tìm kiếm thông tin cập nhật từ Perplexity API
 * @param {string} query - Truy vấn tìm kiếm
 * @param {Object} domainConfig - Cấu hình lĩnh vực (tùy chọn)
 * @returns {Promise<Object>} - Kết quả tìm kiếm
 */
export const searchWithPerplexity = async (query, domainConfig = null) => {
  try {
    console.log(`🔍 Perplexity: Tìm kiếm thông tin cho: "${query}"`);

    const apiKey = getPerplexityApiKey();

    // Tạo context tìm kiếm dựa trên lĩnh vực
    let searchContext = "";
    if (domainConfig && domainConfig.id !== "other") {
      searchContext = `trong lĩnh vực ${domainConfig.name.toLowerCase()}`;
    }

    // Tối ưu hóa query để tìm kiếm thông tin phù hợp cho việc tạo chủ đề Facebook
    const optimizedQuery = `Tìm kiếm thông tin mới nhất và hấp dẫn ${searchContext} về: ${query}

Yêu cầu thông tin để tạo nội dung Facebook:
1. TIN TỨC & XU HƯỚNG: Tin tức nóng hổi, xu hướng đang viral, sự kiện mới nhất
2. SỐ LIỆU THÚ VỊ: Thống kê bất ngờ, con số ấn tượng, dữ liệu gây shock
3. CÂUCHUYỆN THỰC TẾ: Case study thành công, câu chuyện truyền cảm hứng
4. TRANH CÃI & THẢO LUẬN: Vấn đề gây tranh cãi, quan điểm khác nhau
5. MẸO & KINH NGHIỆM: Tips hữu ích, bí quyết thành công, lời khuyên chuyên gia
6. SO SÁNH & ĐÁNH GIÁ: So sánh sản phẩm/dịch vụ, ưu nhược điểm
7. DỰ ĐOÁN TƯƠNG LAI: Dự báo thị trường, triển vọng phát triển

Tập trung vào:
- Thông tin có tính viral cao, dễ chia sẻ
- Nội dung gây tò mò, thu hút tương tác
- Góc nhìn độc đáo, khác biệt
- Thông tin thực tế nhưng thú vị
- Tối đa 400 từ, chỉ bằng tiếng Việt

Ưu tiên thông tin có thể tạo ra các chủ đề Facebook hấp dẫn và viral.`;

    // Tạo system prompt tổng quan, linh hoạt cho mọi lĩnh vực
    let systemPrompt = `Bạn là trợ lý tìm kiếm thông tin chuyên nghiệp, chuyên cung cấp thông tin cập nhật, chính xác và đáng tin cậy. Nhiệm vụ của bạn là tìm kiếm và tổng hợp thông tin theo yêu cầu, tập trung vào dữ liệu thực tế, số liệu cụ thể, và xu hướng mới nhất.`;

    // Thêm context chuyên biệt cho việc tạo nội dung Facebook
    if (domainConfig && domainConfig.id !== "other") {
      systemPrompt += ` Bạn chuyên tìm kiếm thông tin hấp dẫn, có tính viral cao trong lĩnh vực ${domainConfig.name.toLowerCase()}, tập trung vào những thông tin có thể tạo ra nội dung Facebook thu hút và tương tác cao.`;
    } else {
      systemPrompt += ` Bạn chuyên tìm kiếm thông tin hấp dẫn, có tính viral cao, tập trung vào những thông tin có thể tạo ra nội dung Facebook thu hút và tương tác cao.`;
    }

    systemPrompt += ` 

NHIỆM VỤ ĐỘC BIỆT: Thông tin bạn cung cấp sẽ được sử dụng để tạo ra các chủ đề bài viết Facebook. Do đó, hãy ưu tiên:
- Thông tin gây tò mò, bất ngờ
- Xu hướng đang hot, viral
- Câu chuyện có tính nhân văn
- Số liệu ấn tượng, thống kê thú vị
- Tranh cãi tích cực, thảo luận sôi nổi
- Tips/mẹo thực tế, hữu ích

Trả lời ngắn gọn, không quá 400 từ, chỉ bằng tiếng Việt. Tập trung vào thông tin có tiềm năng viral cao nhất.`;

    // Gọi Perplexity API trực tiếp
    const response = await axios.post(
      "https://api.perplexity.ai/chat/completions",
      {
        model: "sonar",
        messages: [
          {
            role: "system",
            content: systemPrompt,
          },
          {
            role: "user",
            content: optimizedQuery,
          },
        ],
        options: {
          stream: false,
        },
        max_tokens: 1000,
        temperature: 0.1, // Giảm temperature để có kết quả chính xác hơn
      },
      {
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${apiKey}`,
        },
        timeout: 30000, // 30 seconds timeout
      }
    );

    // Lấy nội dung từ phản hồi
    if (
      response.data &&
      response.data.choices &&
      response.data.choices.length > 0
    ) {
      const content = response.data.choices[0].message.content;
      console.log("✅ Perplexity: Đã nhận thông tin cập nhật");
      return {
        success: true,
        content: content.trim(),
        source: "perplexity",
        usage: response.data.usage || null,
      };
    } else {
      console.log("⚠️ Perplexity: Không nhận được thông tin hợp lệ");
      return {
        success: false,
        error: "Không nhận được thông tin hợp lệ từ Perplexity.ai",
        source: "perplexity",
      };
    }
  } catch (error) {
    console.error("❌ Perplexity API Error:", error);

    // Xử lý các loại lỗi khác nhau
    let errorMessage = "Lỗi khi tìm kiếm thông tin";

    if (error.code === "ECONNABORTED") {
      errorMessage = "Timeout khi tìm kiếm thông tin";
    } else if (error.response?.status === 429) {
      errorMessage = "Đã vượt quá giới hạn API, vui lòng thử lại sau";
    } else if (error.response?.status === 401) {
      errorMessage = "Lỗi xác thực API";
    } else if (error.response?.status >= 500) {
      errorMessage = "Dịch vụ tìm kiếm tạm thời không khả dụng";
    }

    return {
      success: false,
      error: errorMessage,
      source: "perplexity",
      details: error.message,
    };
  }
};

/**
 * Tìm kiếm thông tin về dự án/bất động sản cụ thể
 * @param {string} projectInfo - Thông tin dự án
 * @param {Object} domainConfig - Cấu hình lĩnh vực
 * @returns {Promise<Object>} - Thông tin chi tiết về dự án
 */
export const searchProjectInfo = async (projectInfo, domainConfig = null) => {
  try {
    if (!projectInfo || projectInfo.trim() === "") {
      return {
        success: false,
        error: "Không có thông tin dự án để tìm kiếm",
      };
    }

    console.log(`🏗️ Perplexity: Tìm kiếm thông tin dự án: "${projectInfo}"`);

    let domainSpecificQuery = "";

    if (domainConfig && domainConfig.id !== "other") {
      // Tạo prompt tổng quan với thông tin lĩnh vực hoạt động
      domainSpecificQuery = `
Tìm kiếm thông tin hấp dẫn và viral về: ${projectInfo}

LĨNH VỰC: ${domainConfig.name}

Yêu cầu thông tin cho Facebook content:
- ĐIỂM NHẤN ĐỘC ĐÁO: Đặc điểm khác biệt, điểm nổi bật gây ấn tượng
- TIN TỨC HOT: Cập nhật mới nhất, sự kiện đáng chú ý
- XU HƯỚNG VIRAL: Trend đang hot trong lĩnh vực ${domainConfig.name.toLowerCase()}
- THỐNG KÊ THÚ VỊ: Số liệu bất ngờ, con số ấn tượng
- CÂUCHUYỆN THÀNH CÔNG: Case study, testimonial, review tích cực
- TRANH CÃI TÍCH CỰC: Vấn đề gây thảo luận, so sánh

Ưu tiên thông tin có thể tạo ra chủ đề Facebook viral, thu hút tương tác cao.`;
    } else {
      // Trường hợp không có domain config hoặc domain là "other"
      domainSpecificQuery = `
Tìm kiếm thông tin viral và hấp dẫn về: ${projectInfo}

Yêu cầu thông tin cho Facebook content:
- ĐIỂM ĐỘC ĐÁO: Đặc điểm khác biệt, điểm nhấn nổi bật
- REVIEW & ĐÁNH GIÁ: Phản hồi tích cực, testimonial thú vị
- TIN TỨC MỚI: Cập nhật hot, sự kiện đáng chú ý
- XU HƯỚNG: Trend liên quan đang viral
- SỐ LIỆU ẤN TƯỢNG: Thống kê bất ngờ, con số thú vị

Tập trung vào thông tin có tiềm năng tạo nội dung Facebook thu hút.`;
    }

    const result = await searchWithPerplexity(
      domainSpecificQuery,
      domainConfig
    );

    if (result.success) {
      console.log("✅ Perplexity: Đã tìm thấy thông tin dự án");
    }

    return result;
  } catch (error) {
    console.error("❌ Error searching project info:", error);
    return {
      success: false,
      error: error.message || "Lỗi khi tìm kiếm thông tin dự án",
    };
  }
};

/**
 * Fallback function khi Perplexity không khả dụng
 * @param {string} query - Truy vấn gốc
 * @returns {Object} - Thông báo fallback
 */
export const perplexityFallback = (query) => {
  return {
    success: false,
    error: "Dịch vụ tìm kiếm thông tin tạm thời không khả dụng",
    fallback: true,
    originalQuery: query,
  };
};
